import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { APIError } from "@/lib/api";
import { useAdminAuthStore } from "@/stores/adminAuthStore";
import { ThemeProvider } from "next-themes";
import { Toaster } from "sonner";
import { AdminLayoutRoute } from "@/routes/AdminLayoutRoute";
import { LoginPage } from "@/pages/auth";
import OAuthCallbackPage from "@/pages/auth/OAuthCallbackPage";
import { DashboardPage } from "@/pages/dashboard";
import { UserListPage, UserDetailPage, UserEditPage } from "@/pages/users";
import {
  TenantListPage,
  TenantDetailPage,
  CreateTenantPage,
  TenantEditPage,
  TenantDepositPage,
} from "@/pages/tenants";
import {
  PaymentVerificationPage,
  PaymentListPage,
  PaymentDetailPage,
} from "@/pages/payments";
import {
  ResourceMonitoringPage,
  // ResourceActionsPage — unrouted, see the commented-out route below.
  ResourceDetailPage,
  ResourceExpiryManagementPage,
  ProvisionVmForUserPage,
  TransferOwnershipPage,
  ProductLineAuditPage,
  ResourceReinstallsPage,
  ResourceReinstallReportPage,
} from "@/pages/resources";
import { AuditLogsPage } from "@/pages/audit";
const NotificationLogsPage = lazy(() =>
  import("@/pages/notification-logs").then((m) => ({
    default: m.NotificationLogsPage,
  })),
);
import {
  OSImagesPage,
  CreateOSImagePage,
  EditOSImagePage,
} from "@/pages/admin/os-images";
import {
  DomainOperationsPage,
  DomainOperationsDashboardPage,
} from "@/pages/domains";
import { TldCatalogListPage } from "@/pages/tld-catalog";
const DomainReportsPage = lazy(() =>
  import("@/pages/domain-reports").then((m) => ({
    default: m.DomainReportsPage,
  })),
);
const GrowthReportPage = lazy(() =>
  import("@/pages/reports").then((m) => ({
    default: m.GrowthReportPage,
  })),
);
const WiServiceReportPage = lazy(() =>
  import("@/pages/reports").then((m) => ({
    default: m.WiServiceReportPage,
  })),
);
import { RegistrarSettingsPage } from "@/pages/registrar-settings";
import { InvitesPage } from "@/pages/admin/InvitesPage";
import {
  InvitationCodeListPage,
  CreateInvitationCodePage,
  InvitationCodeDetailPage,
} from "@/pages/invitation-codes";

const SignupBonusConfigPage = lazy(() =>
  import("@/pages/signup-bonus").then((m) => ({
    default: m.SignupBonusConfigPage,
  })),
);
import { AdminOrdersListPage, AdminOrderDetailPage } from "@/pages/orders";
const AdminEInvoiceListPage = lazy(() =>
  import("@/pages/e-invoices/AdminEInvoiceListPage").then((m) => ({
    default: m.AdminEInvoiceListPage,
  })),
);
const AdminBillingInfoPage = lazy(() =>
  import("@/pages/billing-info/AdminBillingInfoPage").then((m) => ({
    default: m.AdminBillingInfoPage,
  })),
);
const AdminBalanceListPage = lazy(() =>
  import("@/pages/balances/AdminBalanceListPage").then((m) => ({
    default: m.AdminBalanceListPage,
  })),
);
import { ExchangeRatesPage, ExchangeRateHistoryPage } from "@/pages/settings";
import { PricingComponentListPage } from "@/pages/pricing-components/PricingComponentListPage";
import { PricingComponentDetailPage } from "@/pages/pricing-components/PricingComponentDetailPage";
import { ProductLineListPage } from "@/pages/product-lines/ProductLineListPage";

const ProductLineEditPage = lazy(() =>
  import("@/pages/product-lines/ProductLineEditPage").then((m) => ({
    default: m.ProductLineEditPage,
  })),
);
import { PlatformSettingsListPage } from "@/pages/platform-settings/PlatformSettingsListPage";
import { HelperPage } from "@/pages/helper/HelperPage";

// Its own chunk: the guide's text is loaded only by someone who opens the page.
const JobGuidePage = lazy(() =>
  import("@/pages/helper/JobGuidePage").then((m) => ({
    default: m.JobGuidePage,
  })),
);
import { EmailTemplatesListPage } from "@/pages/email-templates/EmailTemplatesListPage";
import { ComponentPricesPage } from "@/pages/pricing/ComponentPricesPage";
import { BillingCycleDiscountListPage } from "@/pages/billing-cycle-discounts/BillingCycleDiscountListPage";
import {
  BillingCycleListPage,
  ExpiryMismatchPage,
} from "@/pages/billing-cycles";
import { ApiKeysListPage } from "@/pages/api-keys";
import { TPaygateSettingsPage } from "@/pages/tpaygate/TPaygateSettingsPage";
import { TPaygateWebhookSimulatorPage } from "@/pages/tpaygate/TPaygateWebhookSimulatorPage";
import { TPaygateWebhookLogPage } from "@/pages/tpaygate/TPaygateWebhookLogPage";

const ProductListPage = lazy(() =>
  import("@/pages/products/ProductListPage").then((m) => ({
    default: m.ProductListPage,
  })),
);
const CreateProductPage = lazy(() =>
  import("@/pages/products/CreateProductPage").then((m) => ({
    default: m.CreateProductPage,
  })),
);
const ProductEditPage = lazy(() =>
  import("@/pages/products/ProductEditPage").then((m) => ({
    default: m.ProductEditPage,
  })),
);
const ProductPricingPage = lazy(() =>
  import("@/pages/products/ProductPricingPage").then((m) => ({
    default: m.ProductPricingPage,
  })),
);
const ProductPricingDetailPage = lazy(() =>
  import("@/pages/products/ProductPricingDetailPage").then((m) => ({
    default: m.ProductPricingDetailPage,
  })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      // `retry: 1` for everything the server answered, as before — but never for a request that
      // never reached it. Whatever drops the first attempt drops the second, and each one costs
      // a full API_TIMEOUT of silence: a blocked network meant 90+1+90 = 181 seconds of skeleton
      // before the console said anything.
      retry: (failureCount, error) => {
        if (error instanceof APIError && error.isTransportFailure()) {
          return false;
        }

        return failureCount < 1;
      },
    },
  },
});

// Every API answer the operator received goes with their session — the button, a sign-out in
// another tab and a failed refresh alike. Query keys carry no operator, so the next one to sign in
// on this tab was shown the previous one's cached lists while their own refetched. Here rather than
// in `clearSessionData` because this module owns the client, and `lib/api` already imports the
// auth store, so importing the client from there would close a cycle.
useAdminAuthStore.subscribe((state, previous) => {
  if (previous.isAuthenticated && !state.isAuthenticated) {
    queryClient.clear();
  }
});

function App() {
  // Light by default, and no system following: with `enableSystem` the stored theme stays
  // "system" until someone picks one, which is neither "light" nor "dark" and is what broke the
  // header toggle. next-themes never persists the default, so this only changes what a browser
  // with no stored choice gets.
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Suspense fallback={null}>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route
                path="/auth/oauth/callback"
                element={<OAuthCallbackPage />}
              />

              {/* Redirect root to admin dashboard */}
              <Route path="/" element={<Navigate to="/admin" replace />} />

              {/* Dashboard */}
              <Route
                path="/admin"
                element={
                  <AdminLayoutRoute>
                    <DashboardPage />
                  </AdminLayoutRoute>
                }
              />

              {/* User Management Routes */}
              <Route
                path="/admin/users"
                element={
                  <AdminLayoutRoute>
                    <UserListPage />
                  </AdminLayoutRoute>
                }
              />
              <Route
                path="/admin/users/:id"
                element={
                  <AdminLayoutRoute>
                    <UserDetailPage />
                  </AdminLayoutRoute>
                }
              />
              <Route
                path="/admin/users/:id/edit"
                element={
                  <AdminLayoutRoute roles={["SuperAdmin"]}>
                    <UserEditPage />
                  </AdminLayoutRoute>
                }
              />

              {/* Tenant Management Routes */}
              <Route
                path="/admin/tenants"
                element={
                  <AdminLayoutRoute>
                    <TenantListPage />
                  </AdminLayoutRoute>
                }
              />
              <Route
                path="/admin/tenants/new"
                element={
                  <AdminLayoutRoute roles={["SuperAdmin"]}>
                    <CreateTenantPage />
                  </AdminLayoutRoute>
                }
              />
              <Route
                path="/admin/tenants/:id"
                element={
                  <AdminLayoutRoute>
                    <TenantDetailPage />
                  </AdminLayoutRoute>
                }
              />
              {/* Declared before "/admin/tenants/:id" for the same reason as move-vm below:
                  otherwise the literal segment is read as a tenant id. SuperAdmin-gated because
                  the correction behind it is — the read-only list is visible to SupportAdmin, but
                  the whole screen exists to make the correction. */}
              <Route
                path="/admin/tenants/product-line-audit"
                element={
                  <AdminLayoutRoute roles={["SuperAdmin"]}>
                    <ProductLineAuditPage />
                  </AdminLayoutRoute>
                }
              />
              {/* The third entry point, and the only one that starts with nothing chosen: opened
                  from the tenant list, where the operator has a hand-over to make but has not
                  singled out either customer yet. Declared before the ":tenantId" routes below so
                  "move-vm" is never read as a tenant id. */}
              <Route
                path="/admin/tenants/move-vm"
                element={
                  <AdminLayoutRoute roles={["SuperAdmin"]}>
                    <TransferOwnershipPage />
                  </AdminLayoutRoute>
                }
              />
              {/* Same page, pre-filled with the customer giving a machine up (from the customer's
                  own page). Picking a machine happens in place — it no longer changes the URL. */}
              <Route
                path="/admin/tenants/:tenantId/transfer-ownership"
                element={
                  <AdminLayoutRoute roles={["SuperAdmin"]}>
                    <TransferOwnershipPage />
                  </AdminLayoutRoute>
                }
              />
              {/* Tenant-scoped on purpose: the transfer needs the CURRENT owner's tenant id to
                  keep that customer out of the receiving-customer search, and the URL then says
                  which account the machine is leaving. */}
              <Route
                path="/admin/tenants/:tenantId/resources/:resourceId/transfer-ownership"
                element={
                  <AdminLayoutRoute roles={["SuperAdmin"]}>
                    <TransferOwnershipPage />
                  </AdminLayoutRoute>
                }
              />
              <Route
                path="/admin/tenants/:id/edit"
                element={
                  <AdminLayoutRoute roles={["SuperAdmin"]}>
                    <TenantEditPage />
                  </AdminLayoutRoute>
                }
              />
              {/* Opening a deposit puts money into a customer's wallet, so the route is
                  SuperAdmin-only — matching the in-handler gate on the create endpoint. */}
              <Route
                path="/admin/tenants/:id/deposit"
                element={
                  <AdminLayoutRoute roles={["SuperAdmin"]}>
                    <TenantDepositPage />
                  </AdminLayoutRoute>
                }
              />

              {/* Payment Management Routes */}
              <Route
                path="/admin/payments"
                element={
                  <AdminLayoutRoute>
                    <PaymentListPage />
                  </AdminLayoutRoute>
                }
              />
              <Route
                path="/admin/payments/verification"
                element={
                  <AdminLayoutRoute>
                    <PaymentVerificationPage />
                  </AdminLayoutRoute>
                }
              />
              <Route
                path="/admin/payments/:paymentId"
                element={
                  <AdminLayoutRoute>
                    <PaymentDetailPage />
                  </AdminLayoutRoute>
                }
              />

              {/* Order Management Routes */}
              <Route
                path="/admin/orders"
                element={
                  <AdminLayoutRoute>
                    <AdminOrdersListPage />
                  </AdminLayoutRoute>
                }
              />
              <Route
                path="/admin/orders/:orderId"
                element={
                  <AdminLayoutRoute>
                    <AdminOrderDetailPage />
                  </AdminLayoutRoute>
                }
              />

              {/* E-Invoice Routes */}
              <Route
                path="/admin/e-invoices"
                element={
                  <AdminLayoutRoute>
                    <AdminEInvoiceListPage />
                  </AdminLayoutRoute>
                }
              />

              {/* Billing Info Route */}
              <Route
                path="/admin/billing-info"
                element={
                  <AdminLayoutRoute>
                    <AdminBillingInfoPage />
                  </AdminLayoutRoute>
                }
              />

              {/* Account Balances Route */}
              <Route
                path="/admin/balances"
                element={
                  <AdminLayoutRoute>
                    <AdminBalanceListPage />
                  </AdminLayoutRoute>
                }
              />

              {/* Product Management Routes */}
              <Route
                path="/admin/products"
                element={
                  <AdminLayoutRoute>
                    <ProductListPage />
                  </AdminLayoutRoute>
                }
              />
              <Route
                path="/admin/products/new"
                element={
                  <AdminLayoutRoute roles={["SuperAdmin", "BillingAdmin"]}>
                    <CreateProductPage />
                  </AdminLayoutRoute>
                }
              />
              <Route
                path="/admin/products/:id"
                element={
                  <AdminLayoutRoute>
                    <ProductEditPage />
                  </AdminLayoutRoute>
                }
              />
              <Route
                path="/admin/products/:id/pricing"
                element={
                  <AdminLayoutRoute>
                    <ProductPricingPage />
                  </AdminLayoutRoute>
                }
              />
              <Route
                path="/admin/products/:id/pricing/:pricingId"
                element={
                  <AdminLayoutRoute>
                    <ProductPricingDetailPage />
                  </AdminLayoutRoute>
                }
              />

              {/* Pricing Components Routes */}
              <Route
                path="/admin/pricing/components"
                element={
                  <AdminLayoutRoute>
                    <PricingComponentListPage />
                  </AdminLayoutRoute>
                }
              />
              <Route
                path="/admin/pricing/components/:id"
                element={
                  <AdminLayoutRoute>
                    <PricingComponentDetailPage />
                  </AdminLayoutRoute>
                }
              />
              <Route
                path="/admin/pricing/prices"
                element={
                  <AdminLayoutRoute>
                    <ComponentPricesPage />
                  </AdminLayoutRoute>
                }
              />

              {/* Billing Cycle Routes */}
              <Route
                path="/admin/billing-cycles"
                element={
                  <AdminLayoutRoute
                    roles={["SuperAdmin", "BillingAdmin", "SupportAdmin"]}
                  >
                    <BillingCycleListPage />
                  </AdminLayoutRoute>
                }
              />

              {/* Same three roles as the list it is reached from: the report only reads, and the
                  Edit button inside it is gated to SuperAdmin on its own. */}
              <Route
                path="/admin/billing-cycles/expiry-mismatches"
                element={
                  <AdminLayoutRoute
                    roles={["SuperAdmin", "BillingAdmin", "SupportAdmin"]}
                  >
                    <ExpiryMismatchPage />
                  </AdminLayoutRoute>
                }
              />

              {/* Resource Monitoring Routes */}
              <Route
                path="/admin/resources"
                element={
                  <AdminLayoutRoute>
                    <ResourceMonitoringPage />
                  </AdminLayoutRoute>
                }
              />
              {/* Unrouted 2026-09-11: resource actions are read one resource at a time from the
                  "Resource Actions" tab of ResourceDetailPage. To bring the cross-resource page
                  back, uncomment this route (declared before "/admin/resources/:id", which would
                  otherwise claim "actions" as a resource id), its import above and its breadcrumb
                  in Breadcrumbs.tsx. */}
              {/* <Route
                path="/admin/resources/actions"
                element={
                  <AdminLayoutRoute roles={["SuperAdmin", "SupportAdmin"]}>
                    <ResourceActionsPage />
                  </AdminLayoutRoute>
                }
              /> */}
              {/* The VM-reinstall list. Same roles as the list endpoint behind it; removing a row
                  is SuperAdmin only and gated on the page. */}
              <Route
                path="/admin/resource-reinstalls"
                element={
                  <AdminLayoutRoute roles={["SuperAdmin", "SupportAdmin"]}>
                    <ResourceReinstallsPage />
                  </AdminLayoutRoute>
                }
              />
              {/* Placeholder page until the report is specified; same roles as the list above. */}
              <Route
                path="/admin/resource-reinstalls/report"
                element={
                  <AdminLayoutRoute roles={["SuperAdmin", "SupportAdmin"]}>
                    <ResourceReinstallReportPage />
                  </AdminLayoutRoute>
                }
              />
              <Route
                path="/admin/resources/:id"
                element={
                  <AdminLayoutRoute roles={["SuperAdmin", "SupportAdmin"]}>
                    <ResourceDetailPage />
                  </AdminLayoutRoute>
                }
              />
              {/* Expiry + billing cycle, side by side. SuperAdmin only, matching both handlers
                  behind it: each refuses every other admin role, so a wider route here would only
                  show an operator two forms they cannot save. */}
              <Route
                path="/admin/resources/:id/expiry"
                element={
                  <AdminLayoutRoute roles={["SuperAdmin"]}>
                    <ResourceExpiryManagementPage />
                  </AdminLayoutRoute>
                }
              />
              <Route
                path="/admin/resources/provision-for-user"
                element={
                  <AdminLayoutRoute roles={["SuperAdmin"]}>
                    <ProvisionVmForUserPage />
                  </AdminLayoutRoute>
                }
              />

              {/* Audit Logs Routes */}
              <Route
                path="/admin/audit"
                element={
                  <AdminLayoutRoute>
                    <AuditLogsPage />
                  </AdminLayoutRoute>
                }
              />

              {/* Notification Log Routes */}
              <Route
                path="/admin/notification-logs"
                element={
                  <AdminLayoutRoute>
                    <NotificationLogsPage />
                  </AdminLayoutRoute>
                }
              />

              {/* API Keys Management Routes */}
              <Route
                path="/admin/api-keys"
                element={
                  <AdminLayoutRoute>
                    <ApiKeysListPage />
                  </AdminLayoutRoute>
                }
              />

              {/* OS Image Management Routes */}
              <Route
                path="/admin/os-images"
                element={
                  <AdminLayoutRoute>
                    <OSImagesPage />
                  </AdminLayoutRoute>
                }
              />
              <Route
                path="/admin/os-images/create"
                element={
                  <AdminLayoutRoute>
                    <CreateOSImagePage />
                  </AdminLayoutRoute>
                }
              />
              {/* No `/admin/os-images/:id` route: the read-only detail page is gone, the edit
                  form is the image's page. */}
              <Route
                path="/admin/os-images/:id/edit"
                element={
                  <AdminLayoutRoute>
                    <EditOSImagePage />
                  </AdminLayoutRoute>
                }
              />

              {/* Domain Operations Routes */}
              <Route
                path="/admin/domains"
                element={
                  <AdminLayoutRoute>
                    <DomainOperationsPage />
                  </AdminLayoutRoute>
                }
              />
              <Route
                path="/admin/domains/operations"
                element={
                  <AdminLayoutRoute>
                    <DomainOperationsDashboardPage />
                  </AdminLayoutRoute>
                }
              />

              {/* Registrar Settings Route */}
              <Route
                path="/admin/registrar-settings"
                element={
                  <AdminLayoutRoute>
                    <RegistrarSettingsPage />
                  </AdminLayoutRoute>
                }
              />

              {/* TLD Catalog Routes */}
              <Route
                path="/admin/tld-catalog"
                element={
                  <AdminLayoutRoute>
                    <TldCatalogListPage />
                  </AdminLayoutRoute>
                }
              />

              {/* Domain Reports Routes */}
              <Route
                path="/admin/domain-reports"
                element={
                  <AdminLayoutRoute
                    roles={["SuperAdmin", "BillingAdmin", "SupportAdmin"]}
                  >
                    <DomainReportsPage />
                  </AdminLayoutRoute>
                }
              />

              {/* Growth Report Routes */}
              <Route
                path="/admin/reports/growth"
                element={
                  <AdminLayoutRoute
                    roles={["SuperAdmin", "BillingAdmin", "SupportAdmin"]}
                  >
                    <GrowthReportPage />
                  </AdminLayoutRoute>
                }
              />

              {/* WiService (n8n) Integration Report Routes */}
              <Route
                path="/admin/reports/wiservice"
                element={
                  <AdminLayoutRoute roles={["SuperAdmin", "SupportAdmin"]}>
                    <WiServiceReportPage />
                  </AdminLayoutRoute>
                }
              />

              {/* Admin Invites Management Routes */}
              <Route
                path="/admin/invites"
                element={
                  <AdminLayoutRoute roles={["SuperAdmin"]}>
                    <InvitesPage />
                  </AdminLayoutRoute>
                }
              />

              {/* Invitation Codes Management Routes */}
              <Route
                path="/admin/invitation-codes"
                element={
                  <AdminLayoutRoute roles={["SuperAdmin"]}>
                    <InvitationCodeListPage />
                  </AdminLayoutRoute>
                }
              />
              <Route
                path="/admin/invitation-codes/new"
                element={
                  <AdminLayoutRoute roles={["SuperAdmin"]}>
                    <CreateInvitationCodePage />
                  </AdminLayoutRoute>
                }
              />
              <Route
                path="/admin/invitation-codes/:id"
                element={
                  <AdminLayoutRoute roles={["SuperAdmin"]}>
                    <InvitationCodeDetailPage />
                  </AdminLayoutRoute>
                }
              />

              {/* Sign-up Bonus Configuration */}
              <Route
                path="/admin/signup-bonus"
                element={
                  <AdminLayoutRoute
                    roles={["SuperAdmin", "BillingAdmin", "SupportAdmin"]}
                  >
                    <SignupBonusConfigPage />
                  </AdminLayoutRoute>
                }
              />

              {/* Settings Routes - Exchange Rates */}
              <Route
                path="/admin/settings"
                element={
                  <Navigate to="/admin/settings/exchange-rates" replace />
                }
              />
              <Route
                path="/admin/settings/exchange-rates"
                element={
                  <AdminLayoutRoute
                    roles={["SuperAdmin", "BillingAdmin", "SupportAdmin"]}
                  >
                    <ExchangeRatesPage />
                  </AdminLayoutRoute>
                }
              />
              <Route
                path="/admin/settings/exchange-rates/:currency/history"
                element={
                  <AdminLayoutRoute
                    roles={["SuperAdmin", "BillingAdmin", "SupportAdmin"]}
                  >
                    <ExchangeRateHistoryPage />
                  </AdminLayoutRoute>
                }
              />

              {/* Product Lines Routes */}
              <Route
                path="/admin/product-lines"
                element={
                  <AdminLayoutRoute>
                    <ProductLineListPage />
                  </AdminLayoutRoute>
                }
              />
              <Route
                path="/admin/product-lines/:id"
                element={
                  <AdminLayoutRoute>
                    <ProductLineEditPage />
                  </AdminLayoutRoute>
                }
              />

              {/* Billing Cycle Discounts Routes */}
              <Route
                path="/admin/billing-discounts"
                element={
                  <AdminLayoutRoute>
                    <BillingCycleDiscountListPage />
                  </AdminLayoutRoute>
                }
              />

              {/* Platform Settings Routes */}
              <Route
                path="/admin/platform-settings"
                element={
                  <AdminLayoutRoute roles={["SuperAdmin"]}>
                    <PlatformSettingsListPage />
                  </AdminLayoutRoute>
                }
              />

              {/* Helper: the maintenance tools, one card each. SuperAdmin only, like the
                  endpoints behind them. Its "Update resource fields" card was the page
                  "/admin/resources/backfill" until 2026-10-13. */}
              <Route
                path="/admin/helper"
                element={
                  <AdminLayoutRoute roles={["SuperAdmin"]}>
                    <HelperPage />
                  </AdminLayoutRoute>
                }
              />
              <Route
                path="/admin/helper/jobs"
                element={
                  <AdminLayoutRoute roles={["SuperAdmin"]}>
                    <JobGuidePage />
                  </AdminLayoutRoute>
                }
              />

              {/* Email Templates Routes */}
              <Route
                path="/admin/email-templates"
                element={
                  <AdminLayoutRoute roles={["SuperAdmin", "SupportAdmin"]}>
                    <EmailTemplatesListPage />
                  </AdminLayoutRoute>
                }
              />

              {/* T-PayGate Integration Routes */}
              <Route
                path="/admin/tpaygate"
                element={
                  <AdminLayoutRoute roles={["SuperAdmin", "SupportAdmin"]}>
                    <TPaygateSettingsPage />
                  </AdminLayoutRoute>
                }
              />
              <Route
                path="/admin/tpaygate/webhook-log"
                element={
                  <AdminLayoutRoute roles={["SuperAdmin", "BillingAdmin"]}>
                    <TPaygateWebhookLogPage />
                  </AdminLayoutRoute>
                }
              />
              <Route
                path="/admin/tpaygate/webhook-simulator"
                element={
                  <AdminLayoutRoute roles={["SuperAdmin"]}>
                    <TPaygateWebhookSimulatorPage />
                  </AdminLayoutRoute>
                }
              />

              {/* Fallback route */}
              <Route path="*" element={<Navigate to="/admin" replace />} />
            </Routes>
          </Suspense>
          <Toaster />
        </BrowserRouter>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
